import React from 'react';
import { Paper, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

export interface MetricCardProps {
  label: string;
  value: string | number;
  context: string;
  /**
   * `softAmber`: light warm surface for “recurrence” at zero
   * `dormant`: very quiet surface for “high-risk” at zero (not alarming)
   */
  tone?: 'default' | 'warning' | 'danger' | 'accent' | 'success' | 'softAmber' | 'dormant';
  /** Stronger type and border when paired with `accent` (e.g. open incidents). */
  visualWeight?: 'default' | 'primary';
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  context,
  tone = 'default',
  visualWeight = 'default',
  onClick,
}) => {
  const theme = useTheme();
  const toneStyles = (() => {
    if (tone === 'danger') {
      return {
        bgcolor: alpha('#9b2c2c', 0.07),
        borderColor: alpha('#c62828', 0.24),
        valueColor: '#8c1d18',
      };
    }
    if (tone === 'warning') {
      return {
        bgcolor: alpha('#d97706', 0.09),
        borderColor: alpha('#d97706', 0.22),
        valueColor: '#7c2d12',
      };
    }
    if (tone === 'softAmber') {
      return {
        bgcolor: alpha('#d97706', 0.04),
        borderColor: alpha('#d97706', 0.14),
        valueColor: alpha('#7c2d12', 0.92),
      };
    }
    if (tone === 'accent') {
      return {
        bgcolor: alpha(theme.palette.primary.main, 0.09),
        borderColor: alpha(theme.palette.primary.main, 0.22),
        valueColor: theme.palette.primary.dark,
      };
    }
    if (tone === 'success') {
      return {
        bgcolor: alpha('#2f855a', 0.07),
        borderColor: alpha('#2f855a', 0.2),
        valueColor: '#256846',
      };
    }
    if (tone === 'dormant') {
      return {
        bgcolor: alpha('#e2e8f0', 0.4),
        borderColor: alpha('#cbd5e1', 0.5),
        valueColor: '#64748b',
      };
    }
    return {
      bgcolor: alpha('#f1f5f9', 0.6),
      borderColor: alpha('#94a3b8', 0.35),
      valueColor: theme.palette.text.primary,
    };
  })();

  const isPrimary = visualWeight === 'primary' && tone === 'accent';
  const primaryExtras =
    isPrimary
      ? {
          p: 2.25,
          minHeight: 120,
          borderWidth: 2,
          boxShadow: '0 2px 12px rgba(29, 78, 145, 0.1)',
        }
      : { p: 2, minHeight: 108, borderWidth: 1, boxShadow: '0 1px 0 rgba(15, 23, 42, 0.04)' };

  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        ...primaryExtras,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: 3,
        cursor: onClick ? 'pointer' : 'default',
        bgcolor: toneStyles.bgcolor,
        borderColor: isPrimary ? alpha(theme.palette.primary.main, 0.42) : toneStyles.borderColor,
        transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
        '&:hover': onClick
          ? {
              transform: 'translateY(-2px)',
              boxShadow: isPrimary
                ? '0 8px 22px rgba(29, 78, 145, 0.12)'
                : '0 10px 20px rgba(15, 23, 42, 0.07)',
            }
          : undefined,
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontWeight: isPrimary ? 700 : 600, fontSize: isPrimary ? 13.5 : undefined, ...(tone === 'dormant' ? { color: 'text.disabled' } : {}) }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: isPrimary ? 36 : 32,
          fontWeight: 800,
          color: toneStyles.valueColor,
          lineHeight: 1.05,
          letterSpacing: isPrimary ? '-0.02em' : undefined,
        }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12.5, ...(tone === 'dormant' ? { color: 'text.disabled' } : {}) }}>
        {context}
      </Typography>
    </Paper>
  );
};

export default MetricCard;
